/**
 * OnboardingWizard — the full new-patient onboarding form.
 *
 * Presentation matches the physician onboarding (PhysicianOnboarding.jsx) and
 * the patient Confirm Details card (PatientConfirmDetails.jsx): one white card
 * with the Parseo mosaic header + 3-step stepper, and a SINGLE scrolling form
 * the patient fills top to bottom — not a one-question-at-a-time wizard. All
 * data, the insurer/pharmacy/provider search widgets, the signature style
 * picker, and the consent acknowledgement are preserved; only the layout
 * changed.
 *
 * Two mounts:
 *   - bridgeMode=true  (BridgeOnboardingPage): full-screen gradient card; the
 *     parent seals + submits, then sends the patient to fill + sign the form.
 *   - bridgeMode=false (App.jsx /profile): rendered inside PatientPortalLayout,
 *     so the full-screen wrapper is dropped and the component POSTs the profile
 *     + consent itself.
 *
 * Inline styles only, to match the rest of patient-ui-app.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import type { CSSProperties } from "react";
import PharmacySearchWidget from "../../shared/ui/survey/widgets/PharmacySearchWidget";
import ProviderSearchWidget from "../../shared/ui/survey/widgets/ProviderSearchWidget";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import {
  PATIENT_CONSENT_ITEMS,
  CONSENT_ACKNOWLEDGEMENT,
  CONSENT_OWNERSHIP_DISCLOSURE,
  CONSENT_FEES_DISCLOSURE,
  buildConsentRecord,
} from "../../lib/consent";
import {
  FORMATS,
  formatValue,
  maskedChange,
  isFormatValid,
  validationMessage,
  suggestDomainTypo,
} from "../../shared/lib/inputFormats";
import { canonicalizeProfileNumbers } from "../../lib/canonicalNumbers";
import InsuranceCardCapture from "./InsuranceCardCapture";
import { routeCandidatesToWizard } from "../../lib/insuranceConfirm";
import { filterInsurers } from "../../lib/insurerFilter";
import type { PortalPatientProfile } from "../../types/shared";

const PROVINCES = ["NS", "NB", "PE", "NL", "ON", "QC", "BC", "AB", "SK", "MB", "NT", "YT", "NU"];
const STEPS = ["Your Details", "Your Form", "Review & Sign"];

// Which of the four "Your details" pages each format-checked field sits on, so
// a bad format is reported on the page that carries the field rather than on
// whichever page the patient happens to be looking at. A field missing here
// reports on page 0 (the safe end: the patient sees it before paging on).
const SECTION_OF_FIELD: Record<string, number> = {
  dateOfBirth: 0, healthCardNumber: 0, email: 0, phone: 0, postalCode: 0,
  planholderDob: 1, planholderEmail: 1, planholderPhone: 1,
  pharmacyPhone: 2, pharmacyFax: 2, doctorPhone: 2, doctorFax: 2,
};

const C = {
  navy: "#182a33",
  sage: "#6b9c8e",
  tan: "#d8c9b6",
  cream: "#f5f0eb",
  border: "#e6dccb",
  muted: "#5b6e7c",
  white: "#ffffff",
};

const inputStyle: CSSProperties = {
  width: "100%", padding: "11px 14px", fontSize: 15,
  border: `1.5px solid ${C.tan}`, borderRadius: 10, outline: "none",
  fontFamily: "system-ui", color: C.navy, boxSizing: "border-box",
};
const labelStyle: CSSProperties = { display: "block", fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 6 };

interface Insurer {
  name: string;
  short_name: string;
  province?: string;
  type?: string;
  aliases?: string[];
  [key: string]: unknown;
}

interface InsurerPickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

// --- Insurer search/select widget ---
function InsurerPicker({ value, onChange, placeholder, autoFocus = false }: InsurerPickerProps) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState<Insurer[]>([]);
  const [allInsurers, setAllInsurers] = useState<Insurer[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/api/insurers/search?q=")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.insurers) {
          setAllInsurers(data.insurers);
          setResults(data.insurers);
        }
      })
      .catch(() => {});
  }, []);

  // Every typed word, in any order, against name, short name, aliases and
  // province ("cross blue", "GSC") — lib/insurerFilter.ts, the bridge's rule.
  useEffect(() => {
    setResults(filterInsurers(allInsurers, query));
  }, [query, allInsurers]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (insurer: Insurer) => {
    setQuery(insurer.name);
    onChange(insurer.name);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        style={inputStyle}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        placeholder={placeholder || "Search or type insurer name..."}
        autoFocus={autoFocus}
      />
      {showDropdown && results.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
            backgroundColor: "white", border: `2px solid ${C.tan}`, borderTop: "none",
            borderRadius: "0 0 10px 10px", maxHeight: 260, overflowY: "auto",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
        >
          {["provincial", "private"].map((type) => {
            const group = results.filter((i) => i.type === type);
            if (group.length === 0) return null;
            return (
              <div key={type}>
                <div
                  style={{
                    padding: "8px 14px", fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: 1, color: C.muted, backgroundColor: C.cream,
                    position: "sticky", top: 0,
                  }}
                >
                  {type === "provincial" ? "Provincial Plans" : "Private Insurers"}
                </div>
                {group.map((ins) => (
                  <div
                    key={ins.name}
                    onClick={() => handleSelect(ins)}
                    style={{
                      padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${C.cream}`,
                      backgroundColor: query === ins.name ? "#e4ecee" : "white",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e4ecee")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = query === ins.name ? "#e4ecee" : "white")}
                  >
                    <div style={{ fontSize: 15, fontWeight: 500, color: C.navy }}>{ins.name}</div>
                    {ins.province && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{ins.province}</div>}
                  </div>
                ))}
              </div>
            );
          })}
          {query.trim() && !allInsurers.some((i) => i.name.toLowerCase() === query.trim().toLowerCase()) && (
            <div
              onClick={() => {
                onChange(query.trim());
                setShowDropdown(false);
              }}
              style={{
                padding: "10px 14px", cursor: "pointer", borderTop: `2px solid ${C.border}`,
                backgroundColor: C.cream, fontSize: 14, color: C.navy,
              }}
            >
              Use "<strong>{query.trim()}</strong>" as entered
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ChoiceButtonsProps {
  options: any[];
  value: unknown;
  onSelect: (value: any) => void;
  getLabel?: (opt: any) => React.ReactNode;
  getValue?: (opt: any) => unknown;
}

// --- Reusable pill/chip button group ---
function ChoiceButtons({ options, value, onSelect, getLabel, getValue }: ChoiceButtonsProps) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {options.map((opt) => {
        const v = getValue ? getValue(opt) : opt;
        const label = getLabel ? getLabel(opt) : opt;
        const selected = value === v;
        return (
          <button
            key={String(v)}
            type="button"
            onClick={() => onSelect(v)}
            style={{
              padding: "10px 22px", borderRadius: 999, fontSize: 15, cursor: "pointer",
              border: `1.5px solid ${selected ? C.navy : C.tan}`,
              backgroundColor: selected ? C.navy : "white",
              color: selected ? "white" : C.navy,
              fontWeight: selected ? 600 : 500,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "26px 0 16px" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
    </div>
  );
}

// Local wizard profile: the camelCase portal profile plus the wizard's own
// working keys. `hasSecondaryInsurance` is a Yes/No/"" STRING here (the shared
// PortalPatientProfile types it as boolean), and hasPublicInsurance is tri-state
// (boolean | undefined) — so both are overridden. certificate/plan-member/
// physician-lookup/consent keys flow through the index signature.
interface WizardProfile extends Omit<PortalPatientProfile, "hasSecondaryInsurance"> {
  hasSecondaryInsurance?: string;
  [key: string]: unknown;
}

interface OnboardingWizardProps {
  onComplete: (data: { profile: WizardProfile }) => void;
  initialProfile?: Partial<WizardProfile> | null;
  bridgeMode?: boolean;
}

// Params for the inline `field(...)` render-helper. `k` indexes the profile,
// `format` names a FORMATS registry key. All but `k`/`label` are optional.
interface FieldRenderProps {
  k: string;
  label: string;
  type?: string;
  placeholder?: string;
  helper?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  full?: boolean;
  options?: string[];
  format?: string;
}

// Every wired-format field must be empty-or-valid to submit. health_card_ns is
// deliberately excluded — out-of-province cards don't match the MSI 10-digit
// shape, so it warns (in-field) but never blocks (user decision).
const FORMAT_FIELDS: Record<string, string> = {
  dateOfBirth: "date_iso",
  postalCode: "postal_ca",
  phone: "phone_na",
  email: "email",
  planholderDob: "date_iso",
  planholderPhone: "phone_na",
  planholderEmail: "email",
  pharmacyPhone: "phone_na",
  pharmacyFax: "phone_na",
  physicianPhone: "phone_na",
  physicianFax: "phone_na",
};

// Patient-facing names for the format-gated boxes, used when Submit is blocked.
const FIELD_LABELS: Record<string, string> = {
  dateOfBirth: "Date of Birth",
  postalCode: "Postal Code",
  phone: "Phone",
  email: "Email",
  planholderDob: "the plan member's Date of Birth",
  planholderPhone: "the plan member's Phone",
  planholderEmail: "the plan member's Email",
  pharmacyPhone: "the pharmacy's Phone",
  pharmacyFax: "the pharmacy's Fax",
  physicianPhone: "the prescriber's Phone",
  physicianFax: "the prescriber's Fax",
};

// Seed state through the display formatter. Inputs render formatValue(raw)
// while the gate validates raw, so a prefilled value differing from its display
// form only by punctuation ("B3H1A1" vs "B3H 1A1") looked correct on screen yet
// held the button disabled with nothing highlighted to fix. See the fuller note
// in PatientConfirmDetails.
function normalizeFormats(p: WizardProfile): WizardProfile {
  const out: WizardProfile = { ...p };
  for (const k of Object.keys(FORMAT_FIELDS)) {
    if (out[k]) out[k] = formatValue(FORMAT_FIELDS[k], out[k]);
  }
  return out;
}

// --- Main component ---
export default function OnboardingWizard({ onComplete, initialProfile, bridgeMode = false }: OnboardingWizardProps) {
  const [profile, setProfile] = useState<WizardProfile>(() => normalizeFormats({
    firstName: "", lastName: "", dateOfBirth: "", gender: "",
    streetAddress: "", suite: "", city: "", province: "NS", postalCode: "",
    phone: "", email: "",
    healthCardNumber: "",
    insuranceCarrier: "", groupNumber: "", memberId: "", planNumber: "",
    certificateNumber: "", planSponsor: "",
    hasPublicInsurance: undefined,
    relationshipToPlanholder: "self",
    planholderFirstName: "", planholderLastName: "", planholderDob: "",
    planholderPhone: "", planholderEmail: "",
    hasSecondaryInsurance: "", secondaryInsuranceCarrier: "",
    pharmacyName: "", pharmacyPhone: "", pharmacyFax: "", pharmacyId: "", pharmacyLocation: "",
    physicianFirstName: "", physicianLastName: "", physicianDisplayName: "",
    physicianPhone: "", physicianFax: "", physicianCity: "", physicianId: "", physicianSpecialty: "",
    signatureFont: "Dancing Script", signatureName: "",
    ...(initialProfile || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = useCallback((field: string, value: unknown) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setTouched((t) => ({ ...t, [field]: true }));
  }, []);

  const emailValid =
    !profile.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(profile.email).trim());
  const emailTypoSuggestion = suggestDomainTypo(profile.email);

  const badFormatKeys = Object.keys(FORMAT_FIELDS).filter(
    (k) => !isFormatValid(FORMAT_FIELDS[k], profile[k])
  );
  const formatsOk = badFormatKeys.length === 0;
  const requiredOk =
    String(profile.firstName || "").trim() &&
    String(profile.lastName || "").trim() &&
    String(profile.dateOfBirth || "").trim();
  const consentOk = !!profile.consent_accepted;
  // When the patient isn't the plan member, the plan member's details are
  // required — they fill the form's subscriber fields and (for forms that need
  // a plan-member signature) drive the split-link two-signer dispatch, which
  // needs the plan member's email.
  const planMemberNeeded =
    !!profile.insuranceCarrier &&
    !!profile.relationshipToPlanholder &&
    profile.relationshipToPlanholder !== "self";
  const planMemberEmailValid =
    !profile.planholderEmail ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(profile.planholderEmail).trim());
  const planMemberOk =
    !planMemberNeeded ||
    !!(
      String(profile.planholderFirstName || "").trim() &&
      String(profile.planholderLastName || "").trim() &&
      String(profile.planholderDob || "").trim() &&
      String(profile.planholderEmail || "").trim() &&
      planMemberEmailValid
    );
  const canComplete = requiredOk && emailValid && consentOk && planMemberOk && formatsOk;

  // "Your details" used to be one long scroll of seven divider-separated
  // blocks (Michael, 2026-10-18: "too long ... should be broken up into a few
  // sections"). It is now four pages inside the same card and the same single
  // submit: nothing about what is collected, validated or saved changed, only
  // how much of it is on screen at once.
  const SECTIONS = ["You", "Insurance", "Pharmacy & doctor", "Signature & consent"];
  const LAST_SECTION = SECTIONS.length - 1;
  const [section, setSection] = useState(0);

  // What each page must satisfy before "Next". Each gate is a SUBSET of
  // canComplete, so a patient can never page past a problem and then be told
  // about it on the last page — and the final submit still re-checks the lot.
  const sectionBlocker = (n: number): string | null => {
    if (n === 0) {
      if (!requiredOk) return "First name, last name, and date of birth are required.";
      if (!emailValid) return "Please check the email address.";
    }
    if (n === 1 && !planMemberOk) {
      return "Plan member name, date of birth, and email are required.";
    }
    if (n === LAST_SECTION && !consentOk) return "Please accept the consent to continue.";
    const bad = badFormatKeys.filter((k) => SECTION_OF_FIELD[k] === n);
    if (bad.length) return `Please check ${bad.map((k) => FIELD_LABELS[k] || k).join(", ")}.`;
    return null;
  };
  const blocker = sectionBlocker(section);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patientId =
        profile.patientId ||
        `${profile.firstName}-${profile.lastName}-${profile.dateOfBirth}`.toLowerCase().replace(/\s+/g, "-");

      const backendProfile = {
        patient_id: patientId,
        first_name: profile.firstName,
        last_name: profile.lastName,
        date_of_birth: profile.dateOfBirth,
        gender: profile.gender || null,
        health_care_number: profile.healthCardNumber,
        province: profile.province,
        phone: profile.phone,
        email: profile.email,
        address: [profile.streetAddress, profile.suite].filter(Boolean).join(", "),
        city: profile.city,
        postal_code: profile.postalCode,
        // Structured insurance sub-doc — the canonical store the auto-fill
        // engine reads (provider / plan / member / group / certificate /
        // sponsor / relationship). Mirrors _normalizeProfile so both onboarding
        // paths persist insurance identically; the flat keys are kept for
        // backwards compat. (group_number / policy_number flat keys aren't on
        // the PatientProfile model — the sub-doc's group_id / plan_number are
        // the persisted home for those.)
        insurance: {
          provider: profile.insuranceCarrier || "",
          plan_number: profile.planNumber || "",
          member_id: profile.memberId || "",
          group_id: profile.groupNumber || "",
          certificate_number: profile.certificateNumber || "",
          plan_sponsor: profile.planSponsor || "",
          relationship: profile.relationshipToPlanholder || "self",
          // Canonical additive keys (Track 1E) — plan_id mirrors plan_number,
          // division_id mirrors the group number, and plans[0] is the primary
          // patient-reported plan. Additive: an old bridge ignores these; a new
          // bridge reads plans[0]. Existing keys above are unchanged.
          plan_id: profile.planNumber || "",
          division_id: profile.groupNumber || "",
          plans: [
            {
              rank: "primary",
              carrier: profile.insuranceCarrier || "",
              plan_id: profile.planNumber || "",
              member_id: profile.memberId || "",
              division_id: profile.groupNumber || "",
              plan_sponsor: profile.planSponsor || "",
              source: "patient_reported",
            },
          ],
        },
        insurance_provider: profile.insuranceCarrier,
        insurance_id: profile.memberId,
        // Provincial drug coverage (Pharmacare etc.) — the existing
        // has_public_insurance boolean, captured here for new patients too.
        has_public_insurance:
          typeof profile.hasPublicInsurance === "boolean" ? profile.hasPublicInsurance : null,
        // Secondary/supplemental private plan — only the boolean + provider are
        // consumed by auto-fill (drives the "other private plan" Yes/No on
        // forms). The model doesn't store the secondary policy/member detail.
        has_supplemental_insurance:
          profile.hasSecondaryInsurance === "Yes"
            ? true
            : profile.hasSecondaryInsurance === "No"
            ? false
            : null,
        supplemental_insurance_provider: profile.secondaryInsuranceCarrier || null,
        // Plan member (when the patient isn't the primary planholder). Fills the
        // subscriber fields on group-insurance forms and drives the split-link
        // two-signer dispatch — the email gives the plan member their own link
        // to sign when the form needs their signature.
        plan_member_relationship: profile.relationshipToPlanholder || "self",
        plan_member_first_name: profile.planholderFirstName || null,
        plan_member_last_name: profile.planholderLastName || null,
        plan_member_dob: profile.planholderDob || null,
        plan_member_email: profile.planholderEmail || null,
        plan_member_phone: profile.planholderPhone || null,
        pharmacy_name: profile.pharmacyName,
        pharmacy_location: profile.pharmacyLocation || "",
        pharmacy_fax: profile.pharmacyFax,
        pharmacy_phone: profile.pharmacyPhone,
        physician_first_name: profile.physicianFirstName,
        physician_last_name: profile.physicianLastName,
        physician_city: profile.physicianCity,
        physician_phone: profile.physicianPhone,
        physician_fax: profile.physicianFax,
        authorization_signature: profile.signatureName || `${profile.firstName} ${profile.lastName}`,
        signature_font: profile.signatureFont,
      };

      // In bridge mode the parent (BridgeOnboardingPage) seals + submits
      // the bundle itself — we don't hit any localhost endpoint here.
      if (bridgeMode) {
        onComplete({ profile: { ...profile, id: patientId, patientId } });
        return;
      }

      const res = await fetch("/api/patient/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Numbers go out canonical (no spaces, no hyphens) — the health-card
        // field is masked as the patient types, and a display-formatted number
        // is not a different number (imp_11cf2120).
        body: JSON.stringify(canonicalizeProfileNumbers(backendProfile)),
      });
      if (res.ok) {
        try {
          await fetch(`/api/patient/${encodeURIComponent(patientId as string)}/consent`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              buildConsentRecord({
                accepted: !!profile.consent_accepted,
              })
            ),
          });
        } catch (e) {
          console.warn("Consent record failed (non-fatal):", e);
        }
        onComplete({ profile: { ...profile, id: patientId, patientId } });
      }
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const title = bridgeMode ? "Welcome to Parseo Health" : "Your profile";
  const subtitle = bridgeMode ? "Let's set up your profile" : "Review and update your information";

  // ── small field render-helper ────────────────────────────────────────
  // INVOKED as a function (`{field({...})}`), never mounted as a <Component/>.
  // Defining a component inside render and mounting it would recreate its type
  // every keystroke and blow away input focus; calling it just returns host
  // elements that reconcile in place. Closes over `profile`/`update`.
  // When `format` names a FORMATS key, the input becomes an as-you-type masked
  // text field: value is rendered through the display formatter, onChange runs
  // maskedChange (commit + caret restore), and inputMode/maxLength/placeholder/
  // autoComplete come from the registry. `type="date"` is swapped to text so
  // the mask (not the native picker) drives entry. A non-empty invalid value
  // shows an inline message (health_card_ns is a warning only — never blocks).
  const field = ({ k, label, type = "text", placeholder, helper, inputMode, full, options, format }: FieldRenderProps) => {
    const FMT = FORMATS as Record<string, any>;
    const fmt = format && FMT[format] ? format : null;
    const rawVal = profile[k] || "";
    const displayVal = fmt ? formatValue(fmt, rawVal) : rawVal;
    const invalid = fmt ? !isFormatValid(fmt, rawVal) : false;
    // Also surface a PREFILLED invalid value — see PatientConfirmDetails.
    // Surface a PREFILLED invalid value too, not only after the patient types:
    // gating on `touched` alone left a bad prefill blocking the button with
    // nothing highlighted. During typing `touched` is already set, so this only
    // adds the untouched case. See PatientConfirmDetails.
    const showError = fmt && invalid;
    const isHcn = fmt === "health_card_ns";
    return (
      <div key={k} style={{ gridColumn: full ? "1 / -1" : "auto" }}>
        <label style={labelStyle}>{label}</label>
        {options ? (
          <select value={(profile[k] as string | undefined) || ""} onChange={(e) => update(k, e.target.value)} style={inputStyle}>
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : fmt ? (
          <input
            type={type === "date" ? "text" : type}
            value={displayVal as string}
            onChange={(e) => maskedChange(fmt, displayVal, e, (v: unknown) => update(k, v))}
            placeholder={placeholder || FMT[fmt].placeholder}
            inputMode={FMT[fmt].inputMode}
            maxLength={FMT[fmt].maxLength}
            autoComplete={type === "date" ? "bday" : FMT[fmt].autoComplete}
            style={inputStyle}
          />
        ) : (
          <input
            type={type}
            value={(profile[k] as string | undefined) || ""}
            onChange={(e) => update(k, e.target.value)}
            placeholder={placeholder}
            inputMode={inputMode}
            style={inputStyle}
          />
        )}
        {showError && (
          <p style={{ color: isHcn ? C.muted : "#c25450", fontSize: 12, margin: "5px 2px 0", lineHeight: 1.4 }}>
            {validationMessage(fmt, rawVal)}
          </p>
        )}
        {helper && <p style={{ color: C.muted, fontSize: 12, margin: "5px 2px 0", lineHeight: 1.4 }}>{helper}</p>}
      </div>
    );
  };

  const sigPreviewName = profile.signatureName || `${profile.firstName} ${profile.lastName}`.trim();

  const card = (
    <div style={{ width: "100%", maxWidth: 640, margin: "0 auto" }}>
      <style>{`
        .ow-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ow-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
        @media (max-width: 600px) {
          .ow-grid2, .ow-grid3 { grid-template-columns: 1fr; }
          .ow-card input, .ow-card select { font-size: 16px !important; min-height: 44px; }
          .ow-steplabel { display: none; }
        }
      `}</style>

      <div className="ow-card" style={{ backgroundColor: C.white, borderRadius: 18, boxShadow: "0 10px 40px rgba(0,0,0,0.10)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "24px 32px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.cream }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <ParseoLogo size={36} />
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h1>
              <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>{subtitle}</p>
            </div>
          </div>

          {bridgeMode && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
              {STEPS.map((s, i) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 28, height: 28, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700,
                      backgroundColor: i === 0 ? C.navy : C.border,
                      color: i === 0 ? "#fff" : C.muted,
                    }}
                  >
                    {i + 1}
                  </div>
                  <span className="ow-steplabel" style={{ fontSize: 13, fontWeight: i === 0 ? 600 : 400, color: i === 0 ? C.navy : C.muted }}>
                    {s}
                  </span>
                  {i < STEPS.length - 1 && <div style={{ width: 18, height: 1, backgroundColor: C.tan }} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "26px 32px" }}>
          {/* Which of the four pages of "Your details" this is. Plain text,
              not a second circles-and-lines stepper: the card header already
              carries one for the whole journey, and two steppers stacked read
              as one confusing thing. */}
          <div
            style={{
              display: "flex", alignItems: "baseline", gap: 8,
              marginBottom: 18, flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: C.sage }}>
              Step {section + 1} of {SECTIONS.length}
            </span>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>{SECTIONS[section]}</span>
          </div>

          {/* Page 1 — You: about + contact */}
          {section === 0 && (<>
          {/* About You */}
          <Divider label="About You" />
          <div className="ow-grid2">
            {field({ k: "firstName", label: "First Name", placeholder: "John" })}
            {field({ k: "lastName", label: "Last Name", placeholder: "Smith" })}
          </div>
          <div className="ow-grid2" style={{ marginTop: 16 }}>
            {field({ k: "dateOfBirth", label: "Date of Birth", type: "date", helper: "YYYY-MM-DD", format: "date_iso" })}
            {field({ k: "gender", label: "Sex", options: ["", "Female", "Male", "Other", "Prefer not to say"] })}
          </div>
          <div style={{ marginTop: 16 }}>
            {field({ k: "healthCardNumber", label: "Health Card Number", placeholder: "10 digits (MSI)", full: true, format: "health_card_ns" })}
          </div>

          {/* Contact */}
          <Divider label="Contact" />
          {field({ k: "streetAddress", label: "Street Address", placeholder: "123 Main Street", full: true })}
          <div style={{ marginTop: 16 }}>
            {field({ k: "suite", label: "Apt / Suite", placeholder: "Optional", full: true })}
          </div>
          <div className="ow-grid3" style={{ marginTop: 16 }}>
            {field({ k: "city", label: "City", placeholder: "Halifax" })}
            {field({ k: "province", label: "Province", options: PROVINCES })}
            {field({ k: "postalCode", label: "Postal Code", placeholder: "B3H 1A1", format: "postal_ca" })}
          </div>
          <div className="ow-grid2" style={{ marginTop: 16 }}>
            {field({ k: "phone", label: "Phone", type: "tel", placeholder: "(902) 555-1234", format: "phone_na" })}
            {field({ k: "email", label: "Email", type: "email", placeholder: "john@example.com", format: "email" })}
          </div>
          {!emailValid && touched.email && (
            <p style={{ color: "#c25450", fontSize: 13, margin: "6px 2px 0" }}>That doesn't look like a valid email address.</p>
          )}
          {emailTypoSuggestion && (
            <button
              type="button"
              onClick={() => update("email", emailTypoSuggestion)}
              style={{
                background: "none", border: "none", padding: "4px 2px 0", cursor: "pointer",
                color: C.sage, fontSize: 13, textDecoration: "underline", fontFamily: "system-ui",
              }}
            >
              Did you mean {emailTypoSuggestion}?
            </button>
          )}

          </>)}

          {/* Page 2 — Insurance */}
          {section === 1 && (<>
          <Divider label="Insurance" />
          {/* The card photo comes BEFORE the boxes, not after them in another
              flow: this page is where a new patient first types their carrier,
              plan and member numbers, and those keystrokes are where the
              transcription errors and the insurer-name mismatches come from.
              Sealed upload + on-device OCR; extracted values drop into the
              boxes below for the patient to confirm and never overwrite
              anything they already typed. Hides itself when there is no live
              bridge session, so the boxes still work on their own. */}
          <InsuranceCardCapture
            onExtracted={(candidates) => {
              const patch = routeCandidatesToWizard(candidates, profile);
              const applied = Object.keys(patch).length;
              if (applied) setProfile((prev) => ({ ...prev, ...patch }));
              return applied;
            }}
          />
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Insurance Provider</label>
            <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 8px", lineHeight: 1.4 }}>Your private drug plan, if you have one.</p>
            <InsurerPicker
              value={profile.insuranceCarrier as string | undefined}
              onChange={(v) => update("insuranceCarrier", v)}
              placeholder="Search by name or province..."
            />
          </div>

          {profile.insuranceCarrier && (
            <>
              <div className="ow-grid2" style={{ marginTop: 16 }}>
                {field({
                  k: "planNumber",
                  label: "Plan / Policy / Group #",
                  placeholder: "Plan number",
                  helper: "Sometimes called policy, contract, or group number.",
                })}
                {field({
                  k: "memberId",
                  label: "Member ID / Certificate #",
                  placeholder: "Member ID",
                  helper: "Sometimes called certificate, ID, or participant number.",
                })}
              </div>
              <div className="ow-grid2" style={{ marginTop: 16 }}>
                {field({ k: "certificateNumber", label: "Certificate Number", placeholder: "If listed separately" })}
                {field({ k: "groupNumber", label: "Group Number", placeholder: "If listed separately" })}
              </div>
              <div style={{ marginTop: 16 }}>
                {field({ k: "planSponsor", label: "Plan Sponsor / Employer", placeholder: "Employer or group name (if applicable)", full: true })}
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Are you the primary planholder?</label>
                <ChoiceButtons
                  options={[
                    { value: "self", label: "Yes, I am" },
                    { value: "spouse", label: "No, my spouse" },
                    { value: "dependent", label: "No, I'm a dependent" },
                    { value: "other", label: "Other" },
                  ]}
                  value={profile.relationshipToPlanholder}
                  onSelect={(v) => update("relationshipToPlanholder", v)}
                  getLabel={(o) => o.label}
                  getValue={(o) => o.value}
                />
              </div>
              {profile.relationshipToPlanholder && profile.relationshipToPlanholder !== "self" && (
                <>
                  <p style={{ color: C.muted, fontSize: 12, margin: "16px 2px 0", lineHeight: 1.4 }}>
                    We need the plan member's details to complete group-insurance forms. If the
                    form requires their signature, we'll email them a secure link to sign.
                  </p>
                  <div className="ow-grid2" style={{ marginTop: 12 }}>
                    {field({ k: "planholderFirstName", label: "Plan Member First Name", placeholder: "First name" })}
                    {field({ k: "planholderLastName", label: "Plan Member Last Name", placeholder: "Last name" })}
                  </div>
                  <div className="ow-grid2" style={{ marginTop: 16 }}>
                    {field({ k: "planholderDob", label: "Plan Member Date of Birth", type: "date", format: "date_iso" })}
                    {field({ k: "planholderPhone", label: "Plan Member Phone", type: "tel", placeholder: "(902) 555-1234", format: "phone_na" })}
                  </div>
                  <div style={{ marginTop: 16 }}>
                    {field({ k: "planholderEmail", label: "Plan Member Email", type: "email", placeholder: "name@example.com", full: true, format: "email" })}
                  </div>
                  {touched.planholderEmail && !planMemberEmailValid && (
                    <p style={{ color: "#c25450", fontSize: 13, margin: "6px 2px 0" }}>
                      That doesn't look like a valid email address.
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {/* Provincial drug coverage — always asked (a patient may have only
              provincial coverage, or both). */}
          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Do you also have provincial drug coverage?</label>
            <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>
              e.g. Pharmacare, Family Pharmacare, or a provincial seniors' plan.
            </p>
            <ChoiceButtons
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              value={profile.hasPublicInsurance}
              onSelect={(v) => update("hasPublicInsurance", v)}
              getLabel={(o) => o.label}
              getValue={(o) => o.value}
            />
          </div>

          {/* Secondary insurance */}
          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Do you have secondary insurance?</label>
            <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>A second drug plan through a spouse or parent.</p>
            <ChoiceButtons
              options={["Yes", "No"]}
              value={profile.hasSecondaryInsurance}
              onSelect={(v) => update("hasSecondaryInsurance", v)}
            />
          </div>
          {profile.hasSecondaryInsurance === "Yes" && (
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Secondary Insurance Company</label>
              <InsurerPicker
                value={profile.secondaryInsuranceCarrier as string | undefined}
                onChange={(v) => update("secondaryInsuranceCarrier", v)}
                placeholder="Search by name or province..."
              />
            </div>
          )}

          </>)}

          {/* Page 3 — Pharmacy & doctor */}
          {section === 2 && (<>
          <Divider label="Pharmacy" />
          <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>
            Search by name or city — we'll save the details for your provider.
          </p>
          <PharmacySearchWidget
            value={profile.pharmacyName as string | undefined}
            onChange={(name) => update("pharmacyName", name)}
            mode="patient"
            pharmacyFieldMapping={{ name: "name", phone: "phone", fax: "fax", address: "address" } as any}
            onBatchFill={((fills: any) => {
              if (fills.name) update("pharmacyName", fills.name);
              if (fills.phone) update("pharmacyPhone", fills.phone);
              if (fills.fax) update("pharmacyFax", fills.fax);
              if (fills.address) update("pharmacyLocation", fills.address);
            }) as any}
          />
          {!profile.pharmacyName && (
            <div style={{ marginTop: 16 }}>
              {field({ k: "pharmacyName", label: "Or type your pharmacy name", placeholder: "Pharmacy name", full: true })}
              <div className="ow-grid2" style={{ marginTop: 16 }}>
                {field({ k: "pharmacyPhone", label: "Pharmacy Phone", type: "tel", format: "phone_na" })}
                {field({ k: "pharmacyFax", label: "Pharmacy Fax", type: "tel", format: "phone_na" })}
              </div>
            </div>
          )}

          {/* Your Doctor */}
          <Divider label="Your Doctor (optional)" />
          <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>
            Helps coordinate your care and referrals. Don't have one? You can skip this.
          </p>
          <ProviderSearchWidget
            value={profile.physicianDisplayName as string | undefined}
            onChange={(name) => update("physicianDisplayName", name)}
            onSelect={(provider) => {
              update("physicianFirstName", provider.firstName);
              update("physicianLastName", provider.lastName);
              update("physicianDisplayName", provider.displayName);
              update("physicianPhone", provider.phone);
              update("physicianFax", provider.fax);
              update("physicianCity", provider.city);
              update("physicianId", provider.id);
              update("physicianSpecialty", provider.specialty);
            }}
            mode="patient"
          />
          {!profile.physicianDisplayName && (
            <div style={{ marginTop: 16 }}>
              <div className="ow-grid2">
                {field({ k: "physicianFirstName", label: "Doctor First Name", placeholder: "First name" })}
                {field({ k: "physicianLastName", label: "Doctor Last Name", placeholder: "Last name" })}
              </div>
              <div className="ow-grid2" style={{ marginTop: 16 }}>
                {field({ k: "physicianPhone", label: "Phone", type: "tel", format: "phone_na" })}
                {field({ k: "physicianFax", label: "Fax", type: "tel", format: "phone_na" })}
              </div>
            </div>
          )}

          {/* Signature */}
          </>)}

          {/* Page 4 — Signature & consent */}
          {section === 3 && (<>
          <Divider label="Signature" />
          {field({ k: "signatureName", label: "Your full name as it should appear", placeholder: "Your full name", full: true })}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Choose a style</label>
            <div className="ow-grid2">
              {[
                { name: "Dancing Script", label: "Elegant" },
                { name: "Great Vibes", label: "Classic" },
                { name: "Sacramento", label: "Flowing" },
                { name: "Allura", label: "Formal" },
              ].map((font) => (
                <button
                  key={font.name}
                  type="button"
                  onClick={() => update("signatureFont", font.name)}
                  style={{
                    padding: "18px 16px", borderRadius: 12, cursor: "pointer",
                    border: profile.signatureFont === font.name ? `2px solid ${C.navy}` : `2px solid ${C.tan}`,
                    backgroundColor: profile.signatureFont === font.name ? "#e4ecee" : "white",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontFamily: `'${font.name}', cursive`, fontSize: 24, color: C.navy, marginBottom: 4 }}>
                    {sigPreviewName || "Your Name"}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>{font.label}</div>
                </button>
              ))}
            </div>
            <link href="https://fonts.googleapis.com/css2?family=Dancing+Script&family=Great+Vibes&family=Sacramento&family=Allura&display=swap" rel="stylesheet" />
          </div>

          {/* Consent */}
          <Divider label="Privacy & Consent" />
          <div style={{ padding: 20, borderRadius: 12, border: `1.5px solid ${C.tan}`, backgroundColor: "white" }}>
            {/* v2 ownership disclosure (Parseo Health Inc.) */}
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
              {CONSENT_OWNERSHIP_DISCLOSURE}
            </div>
            <div style={{ display: "grid", gap: 14 }}>
              {PATIENT_CONSENT_ITEMS.map((item) => (
                <div key={item.key}>
                  <div style={{ fontWeight: 600, color: C.navy, fontSize: 14 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{item.detail}</div>
                </div>
              ))}
              {/* v6 point 10: fees for forms and the payment processor */}
              <div>
                <div style={{ fontWeight: 600, color: C.navy, fontSize: 14 }}>{CONSENT_FEES_DISCLOSURE.title}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{CONSENT_FEES_DISCLOSURE.detail}</div>
              </div>
            </div>
          </div>
          <div
            style={{
              marginTop: 14, padding: 16, borderRadius: 12,
              border: profile.consent_accepted ? `2px solid ${C.navy}` : `1.5px solid ${C.tan}`,
              backgroundColor: profile.consent_accepted ? "#e4ecee" : "white",
            }}
          >
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={(profile.consent_accepted || false) as boolean}
                onChange={(e) => update("consent_accepted", e.target.checked)}
                style={{ marginTop: 2, width: 18, height: 18 }}
              />
              <div style={{ fontWeight: 600, color: C.navy, fontSize: 14, lineHeight: 1.5 }}>{CONSENT_ACKNOWLEDGEMENT}</div>
            </label>
          </div>

          </>)}
        </div>

        {/* Footer — Back / Next across the four pages; the single submit
            lives on the last one. The hint names only what THIS page is
            missing. */}
        <div
          style={{
            padding: "16px 32px", backgroundColor: C.cream, borderTop: `1px solid ${C.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => setSection((n) => Math.max(0, n - 1))}
            disabled={section === 0 || saving}
            style={{
              padding: "11px 18px", fontSize: 14, fontWeight: 600,
              backgroundColor: "transparent",
              color: section === 0 || saving ? C.border : C.navy,
              border: `1.5px solid ${section === 0 || saving ? C.border : C.tan}`,
              borderRadius: 10,
              cursor: section === 0 || saving ? "not-allowed" : "pointer",
              visibility: section === 0 ? "hidden" : "visible",
            }}
          >
            ← Back
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
            {blocker && (
              <span style={{ fontSize: 12, color: C.muted, textAlign: "right" }}>{blocker}</span>
            )}
            {section < LAST_SECTION ? (
              <button
                type="button"
                onClick={() => setSection((n) => Math.min(LAST_SECTION, n + 1))}
                disabled={!!blocker}
                style={{
                  padding: "11px 22px", fontSize: 15, fontWeight: 600,
                  backgroundColor: blocker ? "#bbb" : C.navy,
                  color: "#fff", border: "none", borderRadius: 10,
                  cursor: blocker ? "not-allowed" : "pointer",
                }}
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !canComplete}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "11px 22px", fontSize: 15, fontWeight: 600,
                  backgroundColor: saving || !canComplete ? "#bbb" : C.navy,
                  color: "#fff", border: "none", borderRadius: 10,
                  cursor: saving || !canComplete ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving..." : bridgeMode ? "Continue to your form →" : "Save profile"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (bridgeMode) {
    return (
      <div
        style={{
          minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center",
          padding: "24px 16px", background: "linear-gradient(135deg, #f5f0eb 0%, #e4ecee 100%)",
          boxSizing: "border-box",
        }}
      >
        {card}
      </div>
    );
  }
  return card;
}
